/** The image widget's Display fields: the picture (PNG, JPEG or WebP up to 300 KB), its alt text and fit. */

import { useState, type ReactNode } from 'react';

import type { WidgetDisplay } from './reports-api.ts';

export const MAX_BYTES = 300 * 1024;
export const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];

export function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('The file could not be read.'));
    reader.readAsDataURL(file);
  });
}

export function ImageFields({
  display,
  setDisplay,
}: {
  display: WidgetDisplay;
  setDisplay: (patch: Partial<WidgetDisplay>) => void;
}): ReactNode {
  const [problem, setProblem] = useState<string | null>(null);
  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) return setProblem('Use a PNG, JPEG or WebP image.');
    if (file.size > MAX_BYTES) return setProblem('The image must be 300 KB or smaller.');
    setProblem(null);
    setDisplay({
      image: await readAsDataUrl(file),
      alt: display.alt ?? file.name.replace(/\.[a-z]+$/i, ''),
    });
  };
  return (
    <>
      <label className="rs-field">
        Image (PNG, JPEG or WebP, up to 300 KB)
        <input
          type="file"
          accept={ACCEPTED.join(',')}
          onChange={(event) => void pick(event.target.files?.[0])}
        />
      </label>
      {problem && (
        <p className="rh-access-msg rh-access-error" role="alert">
          {problem}
        </p>
      )}
      <label className="rs-field">
        Description for screen readers
        <input
          value={display.alt ?? ''}
          maxLength={200}
          onChange={(event) => setDisplay({ alt: event.target.value })}
        />
      </label>
      <label className="rs-field">
        Fit
        <select
          value={display.fit ?? 'contain'}
          onChange={(event) => setDisplay({ fit: event.target.value as 'contain' | 'cover' })}
        >
          <option value="contain">Show the whole image</option>
          <option value="cover">Fill the box (may crop)</option>
        </select>
      </label>
    </>
  );
}
